// src/components/ui/Infobulle.tsx — SCE Issues
// Infobulle au survol, au style de celle du graphique « Déclarations par
// mois » (recharts) : fond noir, titre + détail.
//
// Rendue dans <body> en position fixe : les barres du dashboard coupent ce qui
// déborde (overflow hidden, pour leurs coins arrondis), une infobulle rendue
// dedans serait tronquée.
//
// Uniquement sur un appareil à survol (souris) : au toucher, le premier tap
// afficherait la bulle au lieu d'agir. Au téléphone, le clic suffit — il
// ouvre la liste des problèmes concernés.
//
// Au-dessus de l'élément, ou en dessous quand la place manque en haut (une
// bulle haute, comme le code de désignation décodé, en tête de fiche).

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useMediaQuery } from '@/hooks/useMediaQuery'

interface InfobulleProps {
  titre: string
  detail?: string
  /**
   * Plusieurs lignes sous le titre (Lot 9 : les problèmes derrière un compteur
   * de l'accueil). Chacune tient sur une ligne, tronquée au besoin.
   */
  lignes?: string[]
  /** Contenu libre sous le titre (tableau de valeurs), avant les lignes et le détail. */
  contenu?: ReactNode
  /** Largeur maximale en px — défaut 260, assez pour une phrase courte. */
  largeur?: number
  children: ReactNode
  className?: string
  style?: CSSProperties
}

const MARGE_BORD = 100
const ECART = 8

export function Infobulle({ titre, detail, lignes, contenu, largeur, children, className, style }: InfobulleProps): JSX.Element {
  const survol = useMediaQuery('(hover: hover)')
  const ref = useRef<HTMLSpanElement>(null)
  const bulle = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ x: number; haut: number; bas: number } | null>(null)
  const [dessous, setDessous] = useState(false)

  // Mesurée avant d'être peinte : trop haute pour la place au-dessus, et plus
  // de place en dessous → on la retourne.
  useLayoutEffect(() => {
    if (!position || !bulle.current) return
    const hauteur = bulle.current.offsetHeight
    const placeHaut = position.haut - ECART
    const placeBas = window.innerHeight - position.bas - ECART
    setDessous(hauteur > placeHaut && placeBas > placeHaut)
  }, [position])

  // Un défilement déplace l'élément sous une bulle en position fixe : on la
  // retire plutôt que de la laisser flotter au mauvais endroit.
  useEffect(() => {
    if (!position) return
    const cacher = (): void => setPosition(null)
    window.addEventListener('scroll', cacher, true)
    return () => window.removeEventListener('scroll', cacher, true)
  }, [position])

  function montrer(): void {
    if (!survol || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    // Une bulle plus large doit rester plus loin du bord pour ne pas déborder.
    const marge = Math.max(MARGE_BORD, (largeur ?? 0) / 2 + 8)
    const x = Math.min(Math.max(r.left + r.width / 2, marge), window.innerWidth - marge)
    setPosition({ x, haut: r.top, bas: r.bottom })
  }

  return (
    <span
      ref={ref}
      className={className}
      style={style}
      onMouseEnter={montrer}
      onMouseLeave={() => setPosition(null)}
      onFocus={montrer}
      onBlur={() => setPosition(null)}
    >
      {children}
      {position &&
        createPortal(
          <div
            ref={bulle}
            role="tooltip"
            style={{
              position: 'fixed',
              left: position.x,
              top: dessous ? position.bas + ECART : position.haut - ECART,
              transform: dessous ? 'translateX(-50%)' : 'translate(-50%, -100%)',
              // Au-dessus du récap « retour de vacances » (85) et des confirmations (90).
              zIndex: 95,
              pointerEvents: 'none',
              maxWidth: largeur ?? 2 * MARGE_BORD + 60,
              backgroundColor: 'var(--color-black)',
              color: 'var(--color-white)',
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.35,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div style={{ fontWeight: 500 }}>{titre}</div>
            {contenu}
            {lignes?.map((l, i) => (
              <div
                key={i}
                style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {l}
              </div>
            ))}
            {detail && <div style={{ fontSize: 11, opacity: 0.8 }}>{detail}</div>}
          </div>,
          document.body,
        )}
    </span>
  )
}
